import { useRef, useEffect, useCallback, useMemo } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import type { Role } from '../../../../types/role';
import { SOVEREIGN } from '../../../epc/utils/epcProjectVisual';

import { RoleEditorProvider, useRoleEditor } from './useRoleEditor';
import { useRoleEditorContext } from './RoleEditorContext';
import RoleEditorHero from './RoleEditorHero';
import RoleEditorFooter from './RoleEditorFooter';
import RoleEditorSidebar, { ROLE_EDITOR_SECTIONS } from './RoleEditorSidebar';
import SectionPreview from './SectionPreview';
import { useSectionCompletion } from './useSectionCompletion';

import IdentitySection from './sections/IdentitySection';
import DepartmentSection from './sections/DepartmentSection';
import AccessTypeSection from './sections/AccessTypeSection';
import ApprovalChainSection from './sections/ApprovalChainSection';
import PermissionsSection from './sections/PermissionsSection';
import MenuOrderSection from './sections/MenuOrderSection';
import type { RoleEditorSection, RoleEditorFormValues } from './types';
import { useFormContext, useWatch } from 'react-hook-form';

interface Props {
  editingRole?: Role | null;
  onAdd: (role: Role) => void;
  onClose?: () => void;
  department?: string;
}

const SECTION_COMPONENTS: Record<RoleEditorSection, React.FC> = {
  identity: IdentitySection,
  department: DepartmentSection,
  access: AccessTypeSection,
  approval: ApprovalChainSection,
  permissions: PermissionsSection,
  menu: MenuOrderSection,
};

/**
 * RoleEditor — the public entry point.
 *
 * Replaces the old monolithic RoleForm with a composable shell:
 *   <RoleEditorProvider> owns form state + cross-section context
 *   <RoleEditorShell> renders slate-hero + sidebar + active section + previews + footer
 *
 * Aesthetic: light slate-50 canvas, slate-gradient hero (matches DashboardHero),
 * emerald accents on active sidebar steps, framer-motion entry stagger.
 */
export default function RoleEditor({
  editingRole,
  onAdd,
  onClose,
  department,
}: Props) {
  return (
    <RoleEditorProvider
      editingRole={editingRole}
      propDepartment={department}
      onAdd={onAdd}
      onClose={onClose}
    >
      <RoleEditorShell onClose={onClose} />
    </RoleEditorProvider>
  );
}

interface ShellProps {
  onClose?: () => void;
}

function RoleEditorShell({ onClose }: ShellProps) {
  const { activeSection, setActiveSection } = useRoleEditor();
  const { isMobileUser, approvalChain, editingRole } = useRoleEditorContext();
  const { control } = useFormContext<RoleEditorFormValues>();
  const reduceMotion = useReducedMotion();

  // Watch only the fields the completion selector needs — keeps re-renders cheap.
  const values = useWatch({
    control,
    name: ['name', 'description', 'permissions', 'roleType', 'approverRoleId', 'department'],
  });
  const partialValues: Partial<RoleEditorFormValues> = useMemo(
    () => ({
      name: values[0] as string | undefined,
      description: values[1] as string | undefined,
      permissions: (values[2] as Record<string, string[]> | undefined) ?? {},
      roleType: values[3] as RoleEditorFormValues['roleType'],
      approverRoleId: values[4] as string | undefined,
      department: values[5] as string | undefined,
    }),
    [values],
  );

  const completion = useSectionCompletion({
    values: partialValues,
    approvalChain,
    isMobileUser,
    isEditing: !!editingRole,
    activeSection,
  });

  // Visible sections (mobile users skip access + approval).
  const visibleSections = useMemo(
    () => ROLE_EDITOR_SECTIONS.filter((s) => completion[s.key] !== 'na'),
    [completion],
  );
  const totalSteps = visibleSections.length;
  const visibleIndex = useCallback(
    (key: RoleEditorSection) =>
      visibleSections.findIndex((s) => s.key === key) + 1,
    [visibleSections],
  );

  // Scroll the active section into view (cinematic focus shift).
  const mainRef = useRef<HTMLDivElement>(null);
  const sectionRefs = useRef<Map<RoleEditorSection, HTMLDivElement>>(new Map());
  const setSectionRef = useCallback(
    (key: RoleEditorSection) => (el: HTMLDivElement | null) => {
      if (el) sectionRefs.current.set(key, el);
      else sectionRefs.current.delete(key);
    },
    [],
  );

  useEffect(() => {
    const node = sectionRefs.current.get(activeSection);
    if (!node) return;
    node.scrollIntoView({
      behavior: reduceMotion ? 'auto' : 'smooth',
      block: 'start',
    });
  }, [activeSection, reduceMotion]);

  // Build a one-line summary for collapsed previews.
  const summaryFor = useCallback(
    (key: RoleEditorSection): string => {
      switch (key) {
        case 'identity':
          return partialValues.name?.trim()
            ? partialValues.name
            : 'Role name and description';
        case 'department':
          return partialValues.department ?? 'Pick a business unit';
        case 'access': {
          const t = partialValues.roleType;
          if (!t) return 'Pick approver, requester, or both';
          return t === 'both' ? 'Approver + Requester' : t === 'approver' ? 'Approver only' : 'Requester only';
        }
        case 'approval': {
          if (approvalChain.cycle) return 'Cycle detected — needs fixing';
          if (approvalChain.steps.length === 0) return 'No approver picked';
          return `${approvalChain.steps.length} step${approvalChain.steps.length > 1 ? 's' : ''} in chain`;
        }
        case 'permissions': {
          const perms = partialValues.permissions ?? {};
          const modules = Object.values(perms).filter(
            (arr) => Array.isArray(arr) && arr.length > 0,
          ).length;
          const actions = Object.values(perms).reduce(
            (acc, arr) => acc + (Array.isArray(arr) ? arr.length : 0),
            0,
          );
          if (actions === 0) return 'No permissions selected yet';
          return `${modules} module${modules > 1 ? 's' : ''} · ${actions} action${actions > 1 ? 's' : ''}`;
        }
        case 'menu':
          return 'Drag to customise sidebar for users in this role';
      }
    },
    [partialValues, approvalChain],
  );

  // Stagger choreography for body entry.
  const containerVariants = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: { staggerChildren: 0.06, delayChildren: 0.1 },
    },
  };

  const activeStep = visibleIndex(activeSection);
  const activeRoleName = partialValues.name?.trim() || 'Untitled role';
  const mode: 'create' | 'edit' = editingRole ? 'edit' : 'create';

  return (
    <div
      className="relative h-screen flex flex-col overflow-hidden"
      style={{ background: SOVEREIGN.canvas }}
    >
      {/* Scrollable body holds hero + grid; footer stays pinned below */}
      <div className="flex-1 overflow-y-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="max-w-7xl mx-auto">
          {/* Hero */}
          <RoleEditorHero
            roleName={activeRoleName}
            mode={mode}
            currentStep={activeStep}
            totalSteps={totalSteps}
            onBack={() => onClose?.()}
          />

          {/* Grid: sidebar + main */}
          <motion.div
            className="grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-5"
            variants={containerVariants}
            initial="hidden"
            animate="show"
          >
            {/* Sidebar */}
            <motion.aside
              className="lg:sticky lg:top-2 lg:self-start"
              variants={{
                hidden: { opacity: 0, x: -12 },
                show: { opacity: 1, x: 0, transition: { duration: 0.4 } },
              }}
            >
              <RoleEditorSidebar />
            </motion.aside>

            {/* Main column */}
            <motion.main
              ref={mainRef}
              className="flex flex-col gap-4"
              variants={{
                hidden: { opacity: 0, y: 12 },
                show: { opacity: 1, y: 0, transition: { duration: 0.45 } },
              }}
            >
              {visibleSections.map((meta) => {
                const SectionComponent = SECTION_COMPONENTS[meta.key];
                const isActive = meta.key === activeSection;
                const step = visibleIndex(meta.key);
                return (
                  <div key={meta.key} ref={setSectionRef(meta.key)}>
                    {isActive ? (
                      <motion.div
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.25 }}
                      >
                        <SectionComponent />
                      </motion.div>
                    ) : (
                      <SectionPreview
                        step={step}
                        title={meta.label}
                        summary={summaryFor(meta.key)}
                        state={completion[meta.key]}
                        onClick={() => setActiveSection(meta.key)}
                      />
                    )}
                  </div>
                );
              })}

              <div
                className="text-[11px] text-center pt-2"
                style={{ color: SOVEREIGN.kpiCaption }}
              >
                Step {activeStep} of {totalSteps}
              </div>
            </motion.main>
          </motion.div>
        </div>
      </div>

      {/* Footer */}
      <div className="relative z-10">
        <RoleEditorFooter onClose={onClose} />
      </div>
    </div>
  );
}
